import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form5498Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 5498 instructions for Tax Year 2025"
          description="Learn IRS Form 5498 step-by-step instructions for Tax Year 2025. TaxZerone helps you accurately report IRA contributions, rollovers, and recharacterizations."
          keywords=""
          canonical="resources/information-returns/form-5498-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 5498</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report Your IRA Contributions Easily with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498instruction`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-5498-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 5498 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Form 5498</strong>
                        <strong style={{ fontWeight: 600 }}> </strong>reports
                        Traditional IRA, Roth IRA, SEP IRA, SIMPLE IRA
                        contributions, rollovers, conversions,
                        recharacterizations, and year-end account value.
                      </span>
                    </li>
                    <li>
                      <span>
                        {'Recipient copies must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>May 31</strong>
                        {
                          ' following the calendar year being reported. The IRS filing deadline is '
                        }
                        <strong style={{ fontWeight: 600 }}>June 1</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>June 1</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li>
                      <span>
                        {
                          'This article covers line-by-line instructions, including trustee or issuer information and participant details, '
                        }
                        <strong style={{ fontWeight: 600 }}>Box 1</strong>
                        {' (IRA Contributions)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 2 '}</strong>
                        (Rollover Contributions)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 3 '}</strong>
                        (Roth IRA Conversion Amount)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 4 '}</strong>
                        (Recharacterized Contributions)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 5 '}</strong>
                        {'(Fair Market Value of the Account),'}{' '}
                        <strong style={{ fontWeight: 600 }}>{'Box 6 '}</strong>
                        (Life Insurance Cost Included in the Box 1)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 7 '}</strong>
                        (IRA Type Checkbox)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 8</strong>
                        {' (SEP Contributions)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 9</strong>
                        (SIMPLE Contributions)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 10 '}</strong>
                        (Roth IRA Contributions)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 11 '}</strong>
                        (Required Minimum Distribution (RMD)
                        <strong style={{ fontWeight: 600 }}> </strong>
                        for Next Year)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Box 12a '}
                        </strong>
                        (RMD Date)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Box 12b '}
                        </strong>
                        (RMD Amount)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Box 13a '}
                        </strong>
                        (Postponed Contributions)
                        {','}{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Box 13b '}
                        </strong>
                        (Contribution Year)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Box 13c '}
                        </strong>
                        (Code)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Box 14a '}
                        </strong>
                        (Repayments)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Box 14b '}
                        </strong>
                        (Repayment Code)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Box 15a '}
                        </strong>
                        (Fair Market Value of Certain Specified Assets)
                        {', and '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Box 15b '}
                        </strong>
                        (Code(s) ) .
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Late, incorrect, or incomplete filing of '}
                        <strong style={{ fontWeight: 600 }}>Form 5498</strong>
                        {' may result in IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the filer meets electronic filing obligations.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  IRS Form 5498 is used to report Individual Retirement Account
                  (IRA) contributions, rollovers, conversions,
                  recharacterizations, and the fair market value (FMV) of IRA
                  accounts to both the IRS and IRA owners.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The form is informational and helps taxpayers verify the
                  accuracy of their IRA-related transactions reported by
                  financial institutions.
                </Typography>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 5498?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who Needs Form 5498 Instructions?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is the information required to file form 5498?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#import" sx={{ color: '#0202ff' }}>
                          Important deadline of Form 5498
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#fill" sx={{ color: '#0202ff' }}>
                          Form 5498 instructions-how to fill out?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#simple"
                          id="what"
                          sx={{ color: '#0202ff' }}
                        >
                          Simplify your Form 5498 filing with TaxZerone.
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 5498?
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form 5498</span> reports
                    IRA contributions (traditional, Roth, SEP, SIMPLE),
                    rollovers, Roth conversions, recharacterizations, and the
                    FMV of the IRA as of
                    <span style={{ fontWeight: 600 }}> December 31</span> of the
                    tax year.{' '}
                  </li>
                  <li>
                    It is issued by the trustee or issuer of the IRA to the
                    account owner and the IRS.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Form 5498</span> is
                    generally sent by{' '}
                    <span style={{ fontWeight: 600 }}> May 31</span> following
                    the tax year to allow for contributions made up to the tax
                    filing deadline.{' '}
                  </li>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }} id="who">
                  <b>Note:</b> The form is not filed with your federal tax
                  return but serves as an informational document for tax
                  reporting and record-keeping.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who Needs Form 5498 Instructions?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Individuals making IRA contributions or rollovers. </li>
                  <li>
                    Financial institutions and trustees responsible for
                    reporting IRA transactions.{' '}
                  </li>
                  <li>
                    Tax professionals prepare returns involving IRA
                    information.{' '}
                  </li>
                  <li id="whatis">
                    Retirement plan administrators managing IRA accounts.{' '}
                  </li>
                  <li>
                    Investors converting traditional IRAs to Roth IRAs or making
                    recharacterizations.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is the information required to file form 5498?
                </Typography>
                <Typography variant="body1">
                  These are the information that required to file IRS form
                  5498{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Trustee/issuer basic information and TIN </li>
                  <li>Participant basic information, TIN and account number</li>
                  <li>
                    Detailed reporting of contributions by account type (IRA,
                    Roth, SEP, SIMPLE)
                  </li>
                  <li>Rollovers contributions and Roth IRA conversions </li>
                  <li>
                    Recharacterized- contributions and repayments (if any)
                  </li>
                  <li id="import">
                    Required minimum distributions (RMDs) information{' '}
                  </li>
                  <li>Fair market value of the account as of December 31 </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important Deadline for Form 5498
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  The deadline to file{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    tax Form 5498
                  </Link>{' '}
                  for the 2025 tax year is{' '}
                  <span style={{ fontWeight: 600 }}> June 01, 2026 </span>. For
                  full details, visit our{' '}
                  <Link
                    href="/resources/information-returns/form-5498-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 5498 Deadline
                  </Link>{' '}
                  page.{' '}
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Form 5498
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Sending participant copies </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            February 02, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>IRS e-filing </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            June 01, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Paper filing </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            June 01, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Reporting IRA contributions </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            June 01, 2026{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 3, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Need More Time to File Form 5498?
                      </Typography>
                      <Typography variant="body2">
                        No worries! Get a{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          30-day extension
                        </span>{' '}
                        with <span style={{ fontWeight: 600 }}>
                          Form 8809
                        </span>{' '}
                        to avoid penalties and stress-free.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498instructions`}
                        id="fill"
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 5498 instructions-how to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Let see the step-by-step instructions to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498/"
                    sx={{ color: '#0202ff' }}
                  >
                    file form 5498
                  </Link>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Enter the Trustee’s details{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-trustee-img.webp" />
                  <source srcSet="/assets/images/f5498-trustee-img.png" />
                  <img
                    src="/assets/images/f5498-trustee-img.png"
                    loading="lazy"
                    alt="Step 1: Enter the Trustee’s details"
                    width={410}
                    height={242}
                    className="proportional-img"
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the trustee’s name, address, and telephone number
                    along with their Taxpayer Identification Number (TIN).{' '}
                  </li>
                  <li>
                    {' '}
                    Most filers of IRS Form 5498 are banks, credit unions, or
                    financial institutions that manage IRA accounts.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Enter the Participants details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-participants-details-img.webp" />
                  <source srcSet="/assets/images/f5498-participants-details-img.png" />
                  <img
                    src="/assets/images/f5498-participants-details-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Participants details "
                    width={410}
                    height={82}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Participant&apos;s TIN:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    For your protection, this form may show only the last four
                    digits of your TIN (SSN, ITIN, ATIN, or EIN).
                  </li>
                  <li>
                    {' '}
                    However, the trustee or issuer has reported your complete
                    TIN to the IRS.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f5498-participant-name-img.webp" />
                  <source srcSet="/assets/images/f5498-participant-name-img.png" />
                  <img
                    src="/assets/images/f5498-participant-name-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Participants details "
                    width={410}
                    height={242}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Participant&apos;s name, complete address, including
                  street address, city or town, state or province, country, and
                  ZIP or foreign postal code.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Contribution and Account details
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Account Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-account-img.webp" />
                  <source srcSet="/assets/images/f5498-account-img.png" />
                  <img
                    src="/assets/images/f5498-account-img.png"
                    loading="lazy"
                    alt="Account Number"
                    width={410}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you&apos;re filing more than one Form 5498 for the same
                  person, you must include an account number. The IRS also
                  recommends adding an account number on all Form 5498 filings
                  to help with tracking.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: IRA Contributions (Other Than Amounts in Boxes 2–4,
                  8–10, 13a, and 14a)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-irs-contribution-img.webp" />
                  <source srcSet="/assets/images/f5498-irs-contribution-img.png" />
                  <img
                    src="/assets/images/f5498-irs-contribution-img.png"
                    loading="lazy"
                    alt="Box 1: IRA Contributions (Other Than Amounts in Boxes 2–4,
                  8–10, 13a, and 14a)"
                    width={170}
                    height={82}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1">
                  Include all traditional IRA contributions made in 2025 and
                  through April 15, 2026, for the 2025 tax year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Total contributions (even if part is for life
                    insurance){' '}
                  </li>
                  <li>Excess contributions </li>
                  <li>
                    Employer contributions not made under SEP or SIMPLE
                    plans{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Exclude:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>SEP (Box 8), SIMPLE (Box 9), Roth (Box 10)</li>
                  <li>
                    Rollovers (Box 2), Recharacterizations (Box 4), Roth
                    conversions (Box 3){' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Rollover Contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-rollover-img.webp" />
                  <source srcSet="/assets/images/f5498-rollover-img.png" />
                  <img
                    src="/assets/images/f5498-rollover-img.png"
                    loading="lazy"
                    alt="Box 2: Rollover Contributions"
                    width={170}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report rollover contributions to an IRA made in 2026,
                  excluding conversions to a Roth IRA (box 3). Include military
                  death gratuity or SGLI payments to a Roth IRA. Late rollovers
                  go in box 13a.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Roth IRA Conversion Amount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-conversion-amount-img.webp" />
                  <source srcSet="/assets/images/f5498-conversion-amount-img.png" />
                  <img
                    src="/assets/images/f5498-conversion-amount-img.png"
                    loading="lazy"
                    alt="Box 3: Roth IRA Conversion Amount"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter amounts converted from a traditional, SEP, or SIMPLE IRA
                  to a Roth IRA during 2025.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Recharacterized Contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-recharacterized-img.webp" />
                  <source srcSet="/assets/images/f5498-recharacterized-img.png" />
                  <img
                    src="/assets/images/f5498-recharacterized-img.png"
                    loading="lazy"
                    alt="Box 4: Recharacterized Contributions"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report any recharacterized contributions transferred between
                  different types of IRAs, along with any associated earnings or
                  losses.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: FMV of Account
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-fmv-account-img.webp" />
                  <source srcSet="/assets/images/f5498-fmv-account-img.png" />
                  <img
                    src="/assets/images/f5498-fmv-account-img.png"
                    loading="lazy"
                    alt="Box 5: FMV of Account"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report the Fair Market Value of the IRA as of December 31,
                  2025. If the account holder has died, this may reflect the
                  value as of the date of death. If the FMV is shown as $0, the
                  estate’s executor or administrator can request the correct
                  date-of-death value from the financial institution.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Life insurance cost included in Box 1
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-life-insurance-img.webp" />
                  <source srcSet="/assets/images/f5498-life-insurance-img.png" />
                  <img
                    src="/assets/images/f5498-life-insurance-img.png"
                    loading="lazy"
                    alt="Box 6: Life insurance cost included in Box 1"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report the portion of Box 1 contributions used for life
                  insurance within the account.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Checkboxes
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-checkboxes-img.webp" />
                  <source srcSet="/assets/images/f5498-checkboxes-img.png" />
                  <img
                    src="/assets/images/f5498-checkboxes-img.png"
                    loading="lazy"
                    alt="Box 7: Checkboxes"
                    width={338}
                    height={22}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1">
                  Check one of the following:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>IRA:</span> Check this box
                    if you&apos;re reporting information for a Traditional
                    IRA.{' '}
                  </li>
                  <li style={{ fontWeight: 600 }}>
                    SEP: Check this box if reporting for a SEP IRA.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>SIMPLE:</span> Use this
                    box if reporting for a SIMPLE IRA.{' '}
                  </li>
                  <ul>
                    <li>Do not use Form 5498 for SIMPLE 401(k) plans. </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>Roth IRA:</span> Check
                    this box when reporting a Roth IRA.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Roth SEP IRA:</span> Check
                    both &quot;SEP&quot; and &quot;Roth IRA&quot; for a Roth SEP
                    IRA.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Roth SIMPLE IRA:</span>{' '}
                    Check both &quot;SIMPLE&quot; and &quot;Roth IRA&quot; for a
                    Roth SIMPLE IRA.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If unsure of the type, default to “IRA.”{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: SEP Contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-sep-contribution-img.webp" />
                  <source srcSet="/assets/images/f5498-sep-contribution-img.png" />
                  <img
                    src="/assets/images/f5498-sep-contribution-img.png"
                    loading="lazy"
                    alt="Box 8: SEP Contributions"
                    width={170}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report employer contributions made under a SEP plan in 2025,
                  including late contributions for 2024. Do not include
                  contributions made in 2026 for the 2025 tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report only contributions made under a valid SEP arrangement.
                  If employer contributions exceed the SEP formula or
                  aren&apos;t part of a SEP plan, report them in{' '}
                  <span style={{ fontWeight: 600 }}>Box 1</span> instead.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also include self-employed SEP contributions and Roth SEP IRA
                  contributions in{' '}
                  <span style={{ fontWeight: 600 }}> Box 8</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: SIMPLE Contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-simple-img.webp" />
                  <source srcSet="/assets/images/f5498-simple-img.png" />
                  <img
                    src="/assets/images/f5498-simple-img.png"
                    loading="lazy"
                    alt="Box 9: SIMPLE Contributions"
                    width={170}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter employer contributions, including salary deferrals, made
                  to a SIMPLE IRA in 2025—this includes contributions for 2024
                  made during 2025, but not made in{' '}
                  <span style={{ fontWeight: 600 }}>2026</span> for{' '}
                  <span style={{ fontWeight: 600 }}>2025</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Roth IRA Contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-roth-ira-img.webp" />
                  <source srcSet="/assets/images/f5498-roth-ira-img.png" />
                  <img
                    src="/assets/images/f5498-roth-ira-img.png"
                    loading="lazy"
                    alt="Box 10: Roth IRA Contributions "
                    width={170}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report Roth IRA contributions (rollovers from a Qualified
                  Tuition Program) made during 2026 for the 2025 tax year (up to
                  April 15, 2026).
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> These contributions are not deductible on your
                  income tax return.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: Check if RMD for 2026
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-check-rmd-img.webp" />
                  <source srcSet="/assets/images/f5498-check-rmd-img.png" />
                  <img
                    src="/assets/images/f5498-check-rmd-img.png"
                    loading="lazy"
                    alt="Box 11: Check if RMD for 2025 "
                    width={170}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check this box if the participant is required to take a
                  Required Minimum Distribution (RMD) for 2026. You must check
                  the box for the year the participant turns{' '}
                  <span style={{ fontWeight: 600 }}>73</span>, even though the
                  first RMD can be delayed until April 1 of the following year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12a& 12b: RMD Info
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-rmd-info-img.webp" />
                  <source srcSet="/assets/images/f5498-rmd-info-img.png" />
                  <img
                    src="/assets/images/f5498-rmd-info-img.png"
                    loading="lazy"
                    alt="Box 12a& 12b: RMD Info "
                    width={338}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> 12a:</span> Enter the RMD
                    start date for the RMD amount contributed{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>12b:</span> Enter the RMD
                    amount, if you’re reporting additional information under
                    alternative one.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 13a–13c: Postponed/Late Contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-postponed-img.webp" />
                  <source srcSet="/assets/images/f5498-postponed-img.png" />
                  <img
                    src="/assets/images/f5498-postponed-img.png"
                    loading="lazy"
                    alt="Boxes 13a–13c: Postponed/Late Contributions"
                    width={338}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> 13a:</span> Report any
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      postponed contributions
                    </span>{' '}
                    made in 2025 for a{' '}
                    <span style={{ fontWeight: 600 }}>prior tax year</span>,
                    such as contributions allowed due to military service or
                    federally declared disasters. Also report{' '}
                    <span style={{ fontWeight: 600 }}>
                      late rollover contributions
                    </span>{' '}
                    made in 2025, including:
                  </li>
                  <ul>
                    <li>Rollovers certified by the participant </li>
                    <li>Qualified plan loan offsets </li>
                    <li>Disaster-related rollovers </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This amount is not included in Box 1 or Box 2.{' '}
                </Typography>
                <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                  <b>Important:</b> If a participant has postponed contributions
                  for more than one prior year, or also has a late rollover
                  contribution, you must file a{' '}
                  <span style={{ fontWeight: 600 }}>separate Form 5498</span>{' '}
                  for each.
                </Alert>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>13b:</span> Enter the
                    applicable tax year for the contribution which is in Box
                    13a.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> 13c:</span> Use the
                    correct code, in which the contribution made in Box 13a{' '}
                  </li>
                  <li>The code such as: </li>
                  <ul>
                    <li>FD (federally declared disaster) </li>
                    <li>SC (self-certified late rollover) </li>
                    <li>PO (qualified plan loan offset rollover)</li>
                  </ul>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 14a & 14b: Repayments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-repayments-img.webp" />
                  <source srcSet="/assets/images/f5498-repayments-img.png" />
                  <img
                    src="/assets/images/f5498-repayments-img.png"
                    loading="lazy"
                    alt="Boxes 14a & 14b: Repayments "
                    width={338}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> 14a:</span> Report
                    repayments related to birth/adoption, qualified disaster,
                    emergency expenses, or abuse/domestic violence{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> 14b:</span> Use the
                    correct repayment code which is given below:{' '}
                  </li>
                  <ul>
                    <li>BA (birth/adoption), </li>
                    <li>EP (emergency expenses),</li>
                    <li>DA (domestic abuse), </li>
                    <li>TI (terminal illness), </li>
                    <li>QR (qualified reservist) </li>
                  </ul>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 15a & 15b: Specified Assets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-specified-assets-img.webp" />
                  <source srcSet="/assets/images/f5498-specified-assets-img.png" />
                  <img
                    src="/assets/images/f5498-specified-assets-img.png"
                    loading="lazy"
                    alt="Boxes 15a & 15b: Specified Assets "
                    width={338}
                    height={62}
                    className="proportional-img"
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> 15a:</span> Report FMV of
                    specified non-publicly traded assets
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }} id="simple">
                      {' '}
                      15b:
                    </span>{' '}
                    Enter the appropriate codes in which value is entered in Box
                    15a.:
                  </li>
                  <ul>
                    <li>
                      A (private stock), B (private debt), D (real estate), E
                      (partnership interest), G (other), H (more than two assets
                      listed A through G) are held in IRA.{' '}
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Simplify your Form 5498 filing with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  With TaxZerone you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 5498
                  </Link>{' '}
                  easily in 3 simple steps:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  <span style={{ fontWeight: 600 }}> Step 1:</span> Log in to
                  your{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498instructions`}
                    sx={{ color: '#0202ff' }}
                  >
                    TaxZerone Free Account
                  </Link>{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  <span style={{ fontWeight: 600 }}> Step 2:</span> Choose tax
                  Form 5498 and enter the details of Trustee, participant and
                  IRA information
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  <span style={{ fontWeight: 600 }}> Step 3:</span> Review and
                  transmit it to the IRS and send participant copies through
                  ZeroneVault or postal mailing.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on the IRS
                        guidelines. For more details, please refer to the
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1099r.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          {' '}
                          instructions on Form 5498
                        </Link>{' '}
                        and the{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          General Instructions of Information Returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File Form 5498?
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              E-Filing starts at{' '}
              <span style={{ fontWeight: 600 }}>$ 2.49 per</span> form and can
              go as low as{' '}
              <span style={{ fontWeight: 600 }}>$0.59 Per form</span>
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498instructions`}
              >
                Start Filing Form 5498 Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form5498Instructions;
